<template>
  <div class="dash-timeline-card" :class="[`dash-timeline-card--${accent}`]">
    <div class="dash-timeline-card__header d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center gap-2">
        <div class="dash-timeline-card__icon">
          <i :class="headerIcon"></i>
        </div>
        <div>
          <h6 class="dash-timeline-card__title mb-0">{{ title }}</h6>
          <span v-if="subtitle" class="dash-timeline-card__subtitle">{{ subtitle }}</span>
        </div>
      </div>
      <div class="d-flex align-items-center gap-2">
        <span v-if="badge" class="dash-timeline-card__badge">
          {{ badge }}
        </span>
        <button v-if="showAction" type="button" class="dash-timeline-card__action" @click="$emit('action')">
          <i class="bi bi-arrow-right-short"></i>
        </button>
      </div>
    </div>

    <div class="dash-timeline-card__body">
      <div class="timeline-container">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TimelineList",
  props: {
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    accent: {
      type: String,
      default: "danger", // danger, warning, primary, success, indigo, cyan
    },
    badge: {
      type: String,
      default: "",
    },
    showAction: {
      type: Boolean,
      default: false,
    },
    darkMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    headerIcon() {
      if (this.accent === "danger") return "bi bi-exclamation-octagon-fill";
      if (this.accent === "warning") return "bi bi-clock-history";
      if (this.accent === "primary" || this.accent === "indigo") return "bi bi-calendar2-check-fill";
      if (this.accent === "success") return "bi bi-check2-all";
      return "bi bi-list-task";
    },
  },
};
</script>

<style scoped lang="scss">
.dash-timeline-card {
  --tl-accent: #f43f5e;
  --tl-accent-rgb: 244, 63, 94;

  border-radius: 14px;
  background: var(--app-surface-elevated, #1c232b);
  border: 1px solid var(--app-border, rgba(255, 255, 255, 0.08));
  box-shadow: 0 4px 18px -2px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  transition: all 0.25s ease;

  &--danger {
    --tl-accent: #f43f5e;
    --tl-accent-rgb: 244, 63, 94;
  }

  &--warning {
    --tl-accent: #f59e0b;
    --tl-accent-rgb: 245, 158, 11;
  }

  &--primary,
  &--indigo {
    --tl-accent: #6366f1;
    --tl-accent-rgb: 99, 102, 241;
  }

  &--cyan,
  &--info {
    --tl-accent: #0ea5e9;
    --tl-accent-rgb: 14, 165, 233;
  }

  &--success,
  &--emerald {
    --tl-accent: #10b981;
    --tl-accent-rgb: 16, 185, 129;
  }
}

.dash-timeline-card__header {
  padding: 8px 14px;
  border-bottom: 1px solid var(--app-border, rgba(255, 255, 255, 0.06));
  background: rgba(255, 255, 255, 0.015);
}

.dash-timeline-card__icon {
  width: 25px;
  height: 25px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: rgba(var(--tl-accent-rgb), 0.14);
  color: var(--tl-accent);
  border: 1px solid rgba(var(--tl-accent-rgb), 0.25);
}

.dash-timeline-card__title {
  font-size: 13px;
  font-weight: 700;
  color: var(--app-heading, #f1f5f9);
  line-height: 1.2;
}

.dash-timeline-card__subtitle {
  font-size: 10px;
  font-weight: 500;
  color: var(--app-text, #94a3b8);
}

.dash-timeline-card__badge {
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 5px;
  background: rgba(var(--tl-accent-rgb), 0.14);
  color: var(--tl-accent);
  border: 1px solid rgba(var(--tl-accent-rgb), 0.25);
  white-space: nowrap;
}

.dash-timeline-card__action {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--app-border, rgba(255, 255, 255, 0.08));
  color: var(--app-text, #94a3b8);
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--tl-accent-rgb), 0.2);
    color: var(--tl-accent);
  }
}

.dash-timeline-card__body {
  padding: 12px 16px;
  flex: 1;
  height: 260px;
  max-height: 260px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 4px;
  }
}

.timeline-container {
  position: relative;
  display: flex;
  flex-direction: column;
}

/* Light mode */
html[data-theme="light"] {
  .dash-timeline-card {
    background: #ffffff;
    border-color: #e2e8f0;
    box-shadow: 0 4px 15px -2px rgba(0, 0, 0, 0.05);
  }

  .dash-timeline-card__header {
    border-bottom-color: #f1f5f9;
    background: #fafafa;
  }

  .dash-timeline-card__title {
    color: #0f172a;
  }

  .dash-timeline-card__body {
    scrollbar-color: #cbd5e1 transparent;
  }
}
</style>
